import { capture } from '@/handlers/error';
import { GroupRecord } from '@/types/group';
import { isRole, Role } from '@/types/role';
import { UserRecord } from '@/types/user';
import { onIdTokenChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { auth, db } from '../../firebase';

interface IdentityContextType {
    roles: Role[] | null;
    groups: (GroupRecord & { owners?: UserRecord[] })[] | null;
    organization: { name: string; oid: string } | null;
}

const IdentityContext = createContext<IdentityContextType | undefined>(undefined);

export const IdentityProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [roles, setRoles] = useState<Role[] | null>(null);
    const [organization, setOrganization] = useState<{ name: string; oid: string } | null>(null);
    const [groups, setGroups] = useState<(GroupRecord & { owners?: UserRecord[] })[] | null>(null);

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(auth, (user) => {
            if (user) {
                user.getIdTokenResult().then((idTokenResult) => {
                    if (Array.isArray(idTokenResult.claims.roles)) {
                        const validRoles = idTokenResult.claims.roles.filter((role) => isRole(role));
                        setRoles(validRoles.length > 0 ? validRoles : null);
                    }

                    if (idTokenResult.claims.org) {
                        const oid = `${idTokenResult.claims.org}`;

                        try {
                            getDoc(doc(db, 'organizations', oid)).then((doc) => {
                                const name = doc.data()?.name;
                                if (name && typeof name === 'string') {
                                    setOrganization({ name, oid });
                                } else {
                                    setOrganization({ name: oid, oid });
                                }
                            });
                        } catch (error) {
                            capture(error);
                        }
                    }

                    if (idTokenResult.claims.org && user.uid) {
                        try {
                            getDocs(query(collection(db, 'groups'), where('oid', '==', idTokenResult.claims.org), where('members', 'array-contains', user.uid))).then(async (groupsSnapshot) => {
                                const groups: (GroupRecord & { owners?: UserRecord[] })[] = [];
                                for (const group of groupsSnapshot.docs) {
                                    const data = group.data();
                                    const membersSnapshot = await getDocs(collection(db, `groups/${group.id}/members`));
                                    const owners = membersSnapshot.docs.filter((doc) => doc.data().role === 'owner');
                                    groups.push({
                                        ...data,
                                        id: group.id,
                                        name: data.name || group.id,
                                        owners: owners.map((doc) => ({ ...doc.data(), uid: doc.id })),
                                    });
                                }
                                setGroups(groups);
                            });
                        } catch (error) {
                            capture(error);
                        }
                    }
                });
            } else {
                setRoles(null);
                setOrganization(null);
                setGroups(null);
            }
        });

        return unsubscribe;
    }, []);

    return (
        <IdentityContext.Provider
            value={{
                roles,
                groups,
                organization,
            }}
        >
            {children}
        </IdentityContext.Provider>
    );
};

export const useIdentity = (): IdentityContextType => {
    const context = useContext(IdentityContext);
    if (!context) {
        throw new Error('useIdentity must be used within an IdentityProvider');
    }

    return context;
};
